import reactUpdate from 'immutability-helper';
import {ANVILMembraneOrientation} from 'molstar/lib/commonjs/extensions/anvil/behavior';
import {DnatcoNtCs} from 'molstar/lib/commonjs/extensions/dnatco';
import {
  MAQualityAssessment,
  QualityAssessmentPLDDTPreset,
  QualityAssessmentQmeanPreset,
} from 'molstar/lib/commonjs/extensions/model-archive/quality-assessment/behavior';
import {QualityAssessment} from 'molstar/lib/commonjs/extensions/model-archive/quality-assessment/prop';
import {PDBeStructureQualityReport} from 'molstar/lib/commonjs/extensions/pdbe';
import {RCSBAssemblySymmetry, RCSBValidationReport} from 'molstar/lib/commonjs/extensions/rcsb';
import {VolsegVolumeServerConfig} from 'molstar/lib/commonjs/extensions/volumes-and-segmentations';
import {StructureSelection, Structure} from 'molstar/lib/commonjs/mol-model/structure';
import {
  BoxifyVolumeStreaming,
  CreateVolumeStreamingBehavior,
  InitVolumeStreaming,
} from 'molstar/lib/commonjs/mol-plugin/behavior/dynamic/volume-streaming/transformers';
import {PluginConfig} from 'molstar/lib/commonjs/mol-plugin/config';
import {PluginSpec} from 'molstar/lib/commonjs/mol-plugin/spec';
import {StateActions} from 'molstar/lib/commonjs/mol-plugin-state/actions';
import {DownloadStructure} from 'molstar/lib/commonjs/mol-plugin-state/actions/structure';
import {AssignColorVolume} from 'molstar/lib/commonjs/mol-plugin-state/actions/volume';
import {
  PresetStructureRepresentations,
  StructureRepresentationPresetProvider,
} from 'molstar/lib/commonjs/mol-plugin-state/builder/structure/representation-preset';
import {BuiltInTrajectoryFormat} from 'molstar/lib/commonjs/mol-plugin-state/formats/trajectory';
import {StateTransforms} from 'molstar/lib/commonjs/mol-plugin-state/transforms';
import {PluginUIContext} from 'molstar/lib/commonjs/mol-plugin-ui/context';
import {DefaultPluginUISpec, PluginUISpec} from 'molstar/lib/commonjs/mol-plugin-ui/spec';
import {Script} from 'molstar/lib/commonjs/mol-script/script';
import {StateObjectRef, StateObjectSelector} from 'molstar/lib/commonjs/mol-state';
import {Asset} from 'molstar/lib/commonjs/mol-util/assets';

import {AMINO_ACID_TLA} from 'biojs/Codons';

/*
 * This function basically adapts the spec from the Mol* example app with some minor adjustments:
 * https://github.com/molstar/molstar/blob/1f77b19ced4980500d529cdcea322af30d26a359/src/apps/viewer/app.ts#L121-L175
 *
 * Some default actions and behaviors are omitted to keep the UI simpler, the remoteState component is
 * disabled and the initial layout is tweaked to conserve screen real estate.
 */
export function getPluginState(defaultPresetId?: string): PluginUISpec {
  // oxlint-disable-next-line new-cap
  return reactUpdate(DefaultPluginUISpec(), {
    actions: {
      $set: [
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateActions.Structure.EnableModelCustomProps),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateActions.Structure.EnableStructureCustomProps),

        // Volume streaming
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(InitVolumeStreaming),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(BoxifyVolumeStreaming),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(CreateVolumeStreamingBehavior),

        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Data.ParseCif),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Data.ParseCcp4),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Data.ParseDsn6),

        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.TrajectoryFromMmCif),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.TrajectoryFromCifCore),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.TrajectoryFromPDB),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.TransformStructureConformation),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.StructureFromModel),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.StructureFromTrajectory),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.ModelFromTrajectory),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.StructureSelectionFromScript),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureRepresentation3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureSelectionsDistance3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureSelectionsAngle3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureSelectionsDihedral3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureSelectionsLabel3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureSelectionsOrientation3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.ModelUnitcell3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureBoundingBox3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.ExplodeStructureRepresentation3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.SpinStructureRepresentation3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.UnwindStructureAssemblyRepresentation3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.TransparencyStructureRepresentation3DFromScript),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.ClippingStructureRepresentation3DFromScript),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.SubstanceStructureRepresentation3DFromScript),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.ThemeStrengthRepresentation3D),

        // oxlint-disable-next-line new-cap
        PluginSpec.Action(AssignColorVolume),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Volume.VolumeFromCcp4),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Volume.VolumeFromDsn6),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Volume.VolumeFromCube),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Volume.VolumeFromDx),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.VolumeRepresentation3D),
      ],
    },
    behaviors: {
      $push: [
        // oxlint-disable-next-line new-cap
        PluginSpec.Behavior(DnatcoNtCs),
        // oxlint-disable-next-line new-cap
        PluginSpec.Behavior(PDBeStructureQualityReport),
        // oxlint-disable-next-line new-cap
        PluginSpec.Behavior(RCSBAssemblySymmetry),
        // oxlint-disable-next-line new-cap
        PluginSpec.Behavior(RCSBValidationReport),
        // oxlint-disable-next-line new-cap
        PluginSpec.Behavior(ANVILMembraneOrientation),
        // oxlint-disable-next-line new-cap
        PluginSpec.Behavior(MAQualityAssessment),
      ],
    },
    customFormats: {$set: []},
    layout: {
      $set: {
        initial: {
          isExpanded: false,
          showControls: false,
          controlsDisplay: 'reactive',
          regionState: {bottom: 'full', left: 'collapsed', right: 'full', top: 'full'},
        },
      },
    },
    // Set bottom to 'none' to hide logs, but keep all other controls.
    components: {$set: {controls: {bottom: 'none'}, remoteState: 'none'}},
    config: {
      $set: [
        [PluginConfig.General.DisableAntialiasing, PluginConfig.General.DisableAntialiasing.defaultValue],
        [PluginConfig.General.PixelScale, PluginConfig.General.PixelScale.defaultValue],
        [PluginConfig.General.PickScale, PluginConfig.General.PickScale.defaultValue],
        [PluginConfig.General.PickPadding, PluginConfig.General.PickPadding.defaultValue],
        [PluginConfig.General.EnableWboit, PluginConfig.General.EnableWboit.defaultValue],
        [PluginConfig.General.EnableDpoit, PluginConfig.General.EnableDpoit.defaultValue],
        [PluginConfig.General.PreferWebGl1, PluginConfig.General.PreferWebGl1.defaultValue],
        // Try to run even if hardware acceleration is not available.
        [PluginConfig.General.AllowMajorPerformanceCaveat, true],
        [PluginConfig.General.PowerPreference, PluginConfig.General.PowerPreference.defaultValue],
        // Disable full-screen because it takes over the whole screen.
        [PluginConfig.Viewport.ShowExpand, false],
        [PluginConfig.Viewport.ShowControls, PluginConfig.Viewport.ShowControls.defaultValue],
        [PluginConfig.Viewport.ShowSettings, PluginConfig.Viewport.ShowSettings.defaultValue],
        [PluginConfig.Viewport.ShowSelectionMode, PluginConfig.Viewport.ShowSelectionMode.defaultValue],
        [PluginConfig.Viewport.ShowAnimation, PluginConfig.Viewport.ShowAnimation.defaultValue],
        [
          PluginConfig.Viewport.ShowTrajectoryControls,
          PluginConfig.Viewport.ShowTrajectoryControls.defaultValue,
        ],
        // remoteState is set to 'none' above, but set the servers to empty strings to be safe.
        [PluginConfig.State.DefaultServer, ''],
        [PluginConfig.State.CurrentServer, ''],
        [PluginConfig.VolumeStreaming.DefaultServer, ''],
        [PluginConfig.VolumeStreaming.Enabled, false],
        [PluginConfig.Download.DefaultPdbProvider, 'pdbe'],
        [PluginConfig.Download.DefaultEmdbProvider, 'pdbe'],
        [PluginConfig.Structure.DefaultRepresentationPreset, defaultPresetId || ViewerAutoPreset.id],
        [PluginConfig.Structure.SaccharideCompIdMapType, 'default'],
        [VolsegVolumeServerConfig.DefaultServer, ''],
      ],
    },
  });
}

export async function downloadRemoteStructure(
  pluginContext: PluginUIContext,
  url: string,
  format: BuiltInTrajectoryFormat
): Promise<void> {
  // Clear any existing structures first to prevent accumulation
  await pluginContext.clear();

  const defaultParams = DownloadStructure.createDefaultParams(
    pluginContext.state.data.root.obj!,
    pluginContext
  );
  await pluginContext.runTask(
    pluginContext.state.data.applyAction(DownloadStructure, {
      source: {
        name: 'url',
        params: {
          // oxlint-disable-next-line new-cap
          url: Asset.Url(url),
          format,
          isBinary: false,
          // Note: can pass in a label here now, maybe should generate one like `AlphaFold 2 run on ${date}`.
          label: undefined,
          options: {...defaultParams.source.params.options, representationParams: undefined},
        },
      },
    })
  );
}

// TODO: this was copy-pasted from molstar with some minor changes from linting - it is exported but trying to import currently fails.
// oxlint-disable-next-line new-cap
export const ViewerAutoPreset = StructureRepresentationPresetProvider({
  id: 'preset-structure-representation-viewer-auto',
  display: {
    name: 'Automatic (w/ Annotation)',
    group: 'Annotation',
    description:
      'Show standard automatic representation but colored by quality assessment (if available in the model).',
  },
  isApplicable(a: unknown) {
    return (
      // @ts-expect-error
      // oxlint-disable-next-line no-implicit-coercion
      !!a.data.models.some((m) => QualityAssessment.isApplicable(m, 'pLDDT')) || // @ts-expect-error
      // oxlint-disable-next-line no-implicit-coercion
      !!a.data.models.some((m) => QualityAssessment.isApplicable(m, 'qmean'))
    );
  },
  params: () => StructureRepresentationPresetProvider.CommonParams,
  async apply(ref, params, plugin) {
    const structureCell = StateObjectRef.resolveAndCheck(plugin.state.data, ref);
    const structure = structureCell?.obj?.data;
    if (!structureCell || !structure) {
      return {};
    }

    if (structure.models.some((m) => QualityAssessment.isApplicable(m, 'pLDDT'))) {
      return await QualityAssessmentPLDDTPreset.apply(ref, params, plugin);
    } else if (structure.models.some((m) => QualityAssessment.isApplicable(m, 'qmean'))) {
      return await QualityAssessmentQmeanPreset.apply(ref, params, plugin);
    } else {
      return await PresetStructureRepresentations.auto.apply(ref, params, plugin);
    }
  },
});

// oxlint-disable-next-line new-cap
export const SurfacePreset = StructureRepresentationPresetProvider({
  id: 'preset-structure-representation-surface-illustrative',
  display: {
    name: 'Surface (Illustrative)',
    group: 'Surface',
    description:
      'Show a gaussian surface with ~15% opacity and cartoon representation, styled with illustrative colors.',
  },
  isApplicable() {
    return true;
  },
  params: () => StructureRepresentationPresetProvider.CommonParams,
  async apply(ref, params, plugin) {
    const structureCell = StateObjectRef.resolveAndCheck(plugin.state.data, ref);
    const structure = structureCell?.obj?.data;
    if (!structureCell || !structure) {
      return {};
    }
    const ligandComp = await plugin.builders.structure.tryCreateComponentStatic(structureCell, 'ligand', {
      label: 'Ligand',
    });
    const polymerComp = await plugin.builders.structure.tryCreateComponentStatic(structureCell, 'polymer', {
      label: 'Polymer',
    });
    await plugin.state.data.build().commit();

    const {update, builder, typeParams} = StructureRepresentationPresetProvider.reprBuilder(plugin, params);
    const representations: Record<string, StateObjectSelector | undefined> = {};
    const components = {
      polymer: polymerComp,
      ligand: ligandComp,
    };

    if (polymerComp) {
      representations.polymerCartoon = builder.buildRepresentation(
        update,
        polymerComp,
        {
          type: 'cartoon',
          typeParams: {...typeParams, ignoreLight: true},
          color: 'illustrative',
        },
        {tag: 'polymer-cartoon'}
      );

      representations.polymerSurface = builder.buildRepresentation(
        update,
        polymerComp,
        {
          type: 'gaussian-surface',
          typeParams: {...typeParams, alpha: 0.25, ignoreLight: true},
          color: 'illustrative',
        },
        {tag: 'polymer-surface'}
      );
    }

    if (ligandComp) {
      representations.ligandBallAndStick = builder.buildRepresentation(
        update,
        ligandComp,
        {
          type: 'ball-and-stick',
          typeParams: {...typeParams, sizeFactor: 0.15},
          color: 'element-symbol',
        },
        {tag: 'ligand'}
      );
      representations.ligandSurface = builder.buildRepresentation(
        update,
        ligandComp,
        {
          type: 'gaussian-surface',
          typeParams: {...typeParams, alpha: 0.25, ignoreLight: true},
        },
        {tag: 'ligand-surface'}
      );
    }
    // Single commit for all reps
    await update.commit({revertOnError: true});

    // Apply stylized rendering with outline and occlusion effects
    if (plugin.canvas3d) {
      plugin.canvas3d.setProps({
        postprocessing: {
          outline: {
            name: 'on',
            params: {
              scale: 1,
              threshold: 0.33,
              color: {r: 0, g: 0, b: 0},
              includeTransparent: true,
            },
          },
          occlusion: {
            name: 'on',
            params: {
              multiScale: {name: 'on', params: {levels: [5, 11, 17], radius: 5, bias: 0.8}},
              radius: 5,
              bias: 0.8,
              blurKernelSize: 11,
              samples: 32,
              resolutionScale: 1,
              color: {r: 0, g: 0, b: 0},
            },
          },
        },
      });
    }
    // Set interactivity to residue level for better interaction
    plugin.managers.interactivity.setProps({granularity: 'residue'});
    return {representations, components};
  },
});

// Mapping from three-letter amino acid codes to single-letter codes
// Derived from AMINO_ACID_TLA (biojs/Codons.ts) with structure-specific additions
const AMINO_ACID_3TO1: {[key: string]: string} = {
  // Standard amino acids - inverse of AMINO_ACID_TLA
  ...Object.fromEntries(
    Object.entries(AMINO_ACID_TLA)
      .filter(([sla]) => sla !== '*') // Exclude stop codon
      .map(([sla, tla]) => [tla.toUpperCase(), sla])
  ),
  // Structure-specific modified residues not in standard biology mapping
  MSE: 'M', // Selenomethionine (modified Met found in crystal structures)
};

export interface ChainInfo {
  chainId: string; // From auth_asym_id (e.g., "0", "1", "A", "B")
  entityId: string; // From label_entity_id
  sequence: string; // Single-letter amino acid codes
}

/**
 * Extract sequences for all chains in a structure.
 * @param structure The Molstar structure to analyze
 * @returns Array of chain information including sequences
 */
export function getChainSequences(structure: Structure): Array<ChainInfo> {
  const chains: Array<ChainInfo> = [];

  if (structure.models.length === 0) {
    return chains;
  }

  // Process first model only
  const model = structure.models[0];
  const {atomicHierarchy} = model;
  const {chains: chainsTable, atoms: atomsTable} = atomicHierarchy;
  const {chainAtomSegments, residueAtomSegments} = atomicHierarchy;

  // Iterate through each chain
  const chainCount = chainsTable._rowCount;

  for (let chainIndex = 0; chainIndex < chainCount; chainIndex++) {
    const chainId = chainsTable.auth_asym_id?.value
      ? chainsTable.auth_asym_id.value(chainIndex)
      : String(chainIndex);

    const entityId = chainsTable.label_entity_id?.value ? chainsTable.label_entity_id.value(chainIndex) : '1';

    // Get residue range for this chain
    const chainStartAtom = chainAtomSegments.offsets[chainIndex];
    const chainEndAtom = chainAtomSegments.offsets[chainIndex + 1];

    // Map atoms to residues
    const startResidueIndex = residueAtomSegments.index[chainStartAtom];
    const endResidueIndex = residueAtomSegments.index[chainEndAtom - 1] + 1;

    // Build sequence from residues
    const sequence = Array.from({length: endResidueIndex - startResidueIndex}, (_, i) => {
      const residueIndex = startResidueIndex + i;
      // Find the first atom in this residue to get its comp_id
      const residueFirstAtom = residueAtomSegments.offsets[residueIndex];

      // Get comp_id from the atoms table
      const compId =
        typeof atomsTable.label_comp_id?.value === 'function'
          ? atomsTable.label_comp_id.value(residueFirstAtom)
          : 'UNK';

      return AMINO_ACID_3TO1[compId] || 'X';
    });

    chains.push({
      chainId: String(chainId),
      entityId: String(entityId),
      sequence: sequence.join(''),
    });
  }

  return chains;
}

/**
 * Find all chains that match the given protein sequence.
 * @param chains Array of chain information from getChainSequences
 * @param targetSequence The protein sequence to match (single-letter codes)
 * @returns Array of chainIds that match, or empty array if no matches found
 */
export function findChainsBySequence(chains: Array<ChainInfo>, targetSequence: string): Array<string> {
  if (!targetSequence || chains.length === 0) {
    return [];
  }

  // Normalize sequences for comparison (case-insensitive)
  const normalizedTarget = targetSequence.toUpperCase();

  // Find all chains with exact matches
  return chains
    .filter((chain) => {
      const normalizedChain = chain.sequence.toUpperCase();
      return normalizedChain === normalizedTarget;
    })
    .map((chain) => chain.chainId);
}

export function selectStructureResidues(
  pluginContext: PluginUIContext,
  structure: Structure,
  residueStart: number,
  residueEnd: number,
  chainIds?: Array<string>
): void {
  const sel = Script.getStructureSelection((Q) => {
    const queries: Record<string, unknown> = {
      'residue-test': Q.core.rel.inRange([
        Q.struct.atomProperty.macromolecular.auth_seq_id(),
        // AlphaFold structures seem to be 1-indexed and Mol* applies this range inclusively,
        // so adjust the start index but not the end index.
        residueStart + 1,
        residueEnd,
      ]),
    };

    // Add chain filter if provided
    if (chainIds != null && chainIds.length > 0) {
      if (chainIds.length === 1) {
        // Single chain - use simple equality
        queries['chain-test'] = Q.core.rel.eq([
          Q.struct.atomProperty.macromolecular.auth_asym_id(),
          chainIds[0],
        ]);
      } else {
        // Multiple chains - use set membership test
        queries['chain-test'] = Q.core.set.has([
          Q.set(...chainIds),
          Q.struct.atomProperty.macromolecular.auth_asym_id(),
        ]);
      }
    }

    return Q.struct.generator.atomGroups(queries);
  }, structure);
  const loci = StructureSelection.toLociWithSourceUnits(sel);
  pluginContext.managers.interactivity.lociSelects.selectOnly({loci});
}
